/**
 * Admin REST API — /api/v1/admin/* 端点封装
 *
 * 全部请求只有三个出口：adminPost / adminDelete（写，转发 shared 的
 * apiPost / apiDelete）与 envelopeGet（读，自带 fetch）。三者都接在
 * onUnauthorized 上，HTTP 401 一律清本地登录态回登录页 —— 只补一处的话，
 * 表现就是「某些页面会跳、某些不会」。
 *
 * 读操作不用 shared 的 apiGet：后端 Paginated 把 total/page/size 放信封顶层，
 * apiGet 只返回 data 字段会丢分页信息。
 */
import {
  apiPost,
  apiDelete,
  API_BASE,
  getAccessToken,
  ApiError,
  useAuthStore,
  ensureFreshToken,
  forceRefresh,
} from "@yuanchat/shared";

/**
 * 上一次离开管理端是因为登录态失效（而非主动登出）。
 * 登录页读它决定要不要提示「会话已过期」—— 不给提示，用户只会看到
 * 自己莫名其妙被弹回登录页。
 */
let sessionExpired = false;

/** 取出并清掉「会话已过期」标记（读一次即消费，刷新页面不再重复提示）。 */
export function takeSessionExpired(): boolean {
  const was = sessionExpired;
  sessionExpired = false;
  return was;
}

/**
 * 登录态失效（HTTP 401）的统一处置。
 *
 * 只需清掉本地登录态：App 的 RequireAuth 订阅了 isAuthenticated，
 * 置 false 的那一刻就渲染 `<Navigate to="/login" replace />`。
 *
 * 清而不吞 —— 错误照旧抛回调用方，该报的错还是会报，不会留下一个
 * 「空列表 + 没有任何提示」的页面。
 *
 * 登录接口不走本模块（authStore 直接调 shared 的 apiPost），
 * 所以密码输错拿到的 401 不会把人踢走，错误提示照常显示。
 */
function onUnauthorized(): void {
  sessionExpired = true;
  useAuthStore.getState().clearSession();
}

/** 把 401 接到 onUnauthorized 上，其余错误原样抛出。 */
async function guard<T>(call: Promise<T>): Promise<T> {
  try {
    return await call;
  } catch (err) {
    if (err instanceof ApiError && err.code === 401) onUnauthorized();
    throw err;
  }
}

/** 写操作出口：shared apiPost + 401 处置。 */
function adminPost<T>(path: string, body: unknown): Promise<T> {
  return guard(apiPost<T>(path, body));
}

/** 写操作出口：shared apiDelete + 401 处置。 */
function adminDelete<T>(path: string): Promise<T> {
  return guard(apiDelete<T>(path));
}

export interface AdminUser {
  id: string;
  phone?: string;
  email?: string;
  short_id: number;
  nickname: string;
  avatar_url?: string;
  status: number; // 1=正常 2=封禁
  role: number; // 0=普通 1=admin
  created_at: string;
  last_login_at?: string;
}

export interface AdminConversation {
  id: string;
  type: number; // 1=单聊 2=群聊
  name: string;
  member_count: number;
  created_at: string;
}

export interface AdminMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_nickname: string;
  message_type: number; // 1=text 2=image 3=file 4=voice 8=sticker
  content: string; // JSONB 原文（文本消息为 {"text":"..."}）
  seq: number;
  created_at: string;
}

export interface AuditLog {
  id: string;
  actor_id: string;
  actor_nickname: string;
  action: string;
  target_type: string;
  target_id: string;
  detail: string;
  created_at: string;
}

export interface Page<T> {
  list: T[];
  total: number;
}

const qs = (params: Record<string, string | number | undefined>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

/** 后端响应信封：data 与 total 顶层平级（total 只有分页端点才有）。 */
interface Envelope<T> {
  code: number;
  message: string;
  data: T | null;
  total?: number;
}

/** 发一次请求并原样取回信封（不判 code，交给 envelopeGet 统一处置）。 */
async function fetchEnvelope<T>(path: string, token: string | null): Promise<Envelope<T>> {
  const res = await fetch(API_BASE + path, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  return (await res.json()) as Envelope<T>;
}

/**
 * 读操作出口：取回完整信封（含 total），并复刻 shared request 的令牌纪律 ——
 * 发请求前先补刷新，拿到 401 再强制刷一次重试一遍，仍是 401 才算登录态真没了。
 *
 * 少了刷新这步，access token 只是到期（refresh 还有效）也会被当成掉线踢回
 * 登录页，等于把 15 分钟的 access 寿命变成了会话寿命。
 */
async function envelopeGet<T>(path: string): Promise<Envelope<T>> {
  const fresh = await ensureFreshToken();
  const token = fresh ?? getAccessToken();

  let json = await fetchEnvelope<T>(path, token);
  if (json.code === 401 && token) {
    const renewed = await forceRefresh();
    if (renewed) json = await fetchEnvelope<T>(path, renewed);
  }
  if (json.code === 401) onUnauthorized();
  if (json.code !== 0) {
    throw new ApiError(json.code, json.message || "Request failed");
  }
  return json;
}

/** 分页 GET：信封 data 为数组，total 为总数。 */
async function pagedGet<T>(path: string): Promise<Page<T>> {
  const json = await envelopeGet<T[]>(path);
  return { list: json.data ?? [], total: json.total ?? 0 };
}

/** 单对象 GET：信封 data 为对象而非数组。 */
async function pagedGetSingle<T>(path: string): Promise<T> {
  const json = await envelopeGet<T>(path);
  return json.data as T;
}

export function listUsers(q: string, page: number, size = 20) {
  return pagedGet<AdminUser>(`/api/v1/admin/users${qs({ q, page, size })}`);
}

export function banUser(id: string) {
  return adminPost<{ banned: boolean; kicked_connections: number }>(
    `/api/v1/admin/users/${id}/ban`,
    {},
  );
}

export function unbanUser(id: string) {
  return adminDelete<{ banned: boolean }>(`/api/v1/admin/users/${id}/ban`);
}

/** 重置用户头像（avatar_url 置空，恢复默认头像；旧对象由 GC 通道回收） */
export function resetUserAvatar(id: string) {
  return adminPost<{ reset: boolean }>(`/api/v1/admin/users/${id}/reset-avatar`, {});
}

export function listConversations(q: string, type: number, page: number, size = 20) {
  return pagedGet<AdminConversation>(
    `/api/v1/admin/conversations${qs({ q, type: type || undefined, page, size })}`,
  );
}

export function dissolveConversation(id: string) {
  return adminPost<{ dissolved: boolean; notified_members: number }>(
    `/api/v1/admin/conversations/${id}/dissolve`,
    {},
  );
}

export function listMessages(q: string, page: number, size = 20) {
  return pagedGet<AdminMessage>(`/api/v1/admin/messages${qs({ q, page, size })}`);
}

export function listFlaggedMessages(page: number, size = 20) {
  return pagedGet<AdminMessage>(`/api/v1/admin/messages${qs({ flagged: "true", page, size })}`);
}

export function deleteMessage(id: string) {
  return adminDelete<{ deleted: boolean }>(`/api/v1/admin/messages/${id}`);
}

export function clearMessageFlag(id: string) {
  return adminDelete<{ flagged: boolean }>(`/api/v1/admin/messages/${id}/flag`);
}

export interface AdminStickerPack {
  id: string;
  name: string;
  cover_url: string | null;
  is_official: boolean;
  flagged: boolean;
  taken_down: boolean;
  owner_name: string | null;
  sticker_count: number;
  created_at: string;
}

export function listFlaggedPacks(page: number, size = 20) {
  return pagedGet<AdminStickerPack>(
    `/api/v1/admin/sticker-packs${qs({ flagged: "true", page, size })}`,
  );
}

/** 全量检索表情包（q 为包名模糊匹配，空串列出最新；不含 flagged 过滤） */
export function listStickerPacks(q: string, page: number, size = 20) {
  return pagedGet<AdminStickerPack>(`/api/v1/admin/sticker-packs${qs({ q, page, size })}`);
}

export function takedownPack(id: string) {
  return adminPost<{ taken_down: boolean }>(`/api/v1/admin/sticker-packs/${id}/takedown`, {});
}

/** 恢复表情包上架（清 taken_down，商城重新展示） */
export function untakedownPack(id: string) {
  return adminPost<{ taken_down: boolean }>(`/api/v1/admin/sticker-packs/${id}/untakedown`, {});
}

/** 切换表情包官方标识（显式目标布尔，避免并发下 toggle 歧义） */
export function setPackOfficial(id: string, isOfficial: boolean) {
  return adminPost<{ is_official: boolean }>(`/api/v1/admin/sticker-packs/${id}/official`, {
    is_official: isOfficial,
  });
}

export function clearPackFlag(id: string) {
  return adminDelete<{ flagged: boolean }>(`/api/v1/admin/sticker-packs/${id}/flag`);
}

export interface AdminReport {
  id: string;
  reporter_id: string;
  reporter_nickname: string;
  target_type: string; // message | user
  target_id: string;
  reason: string;
  status: number; // 0=待处理 1=已保留 2=已删除
  created_at: string;
}

export function listReports(status: number, page: number, size = 20) {
  return pagedGet<AdminReport>(`/api/v1/admin/reports${qs({ status, page, size })}`);
}

export function handleReport(id: string, action: "keep" | "delete") {
  return adminPost<{ handled: boolean }>(`/api/v1/admin/reports/${id}/handle`, { action });
}

export function listAuditLogs(action: string, page: number, size = 20) {
  return pagedGet<AuditLog>(`/api/v1/admin/audit-logs${qs({ action, page, size })}`);
}

/** 消息媒体预签名响应（管理端专用读通道） */
export interface MessageMedia {
  url: string;
  expires_in: number;
  message_type: number;
  object_key: string;
  file_name?: string;
  width?: number;
  height?: number;
  duration?: number;
}

/**
 * 获取消息媒体的短期预签名下载 URL。
 * 走 /admin/messages/:id/media 专用通道，不依赖举报人上下文。
 */
export function getMessageMedia(id: string) {
  return pagedGetSingle<MessageMedia>(`/api/v1/admin/messages/${id}/media`);
}

/** UGC 敏感词命中记录（昵称 / bio / 群名 / 群公告 / 朋友圈动态与评论审核队列） */
export interface FlaggedUGC {
  id: string;
  ugc_type: string; // nickname | bio | group_name | announcement | moment_post | moment_comment
  content: string;
  hit_word: string;
  user_id?: string;
  conversation_id?: string;
  /** 命中内容所在那一行的 id；朋友圈两类靠它定位要删的帖子/评论 */
  target_id?: string;
  handled_at?: string;
  created_at: string;
}

/** 分页检索 UGC 敏感词命中记录（handled: false=待处理 true=已处置 all=全部） */
export function listFlaggedUGC(handled: "false" | "true" | "all", page: number, size = 20) {
  return pagedGet<FlaggedUGC>(`/api/v1/admin/flagged-ugc${qs({ handled, page, size })}`);
}

/** 强制重置命中的 UGC：昵称重置为默认昵称，bio / 群名 / 公告清空 */
export function resetFlaggedUGC(id: string) {
  return adminPost<{ reset: boolean }>(`/api/v1/admin/flagged-ugc/${id}/reset`, {});
}

/** 放行命中的 UGC（内容维持原样，记录关闭） */
export function dismissFlaggedUGC(id: string) {
  return adminDelete<{ dismissed: boolean }>(`/api/v1/admin/flagged-ugc/${id}`);
}

/** 概览用户维度计数 */
export interface AdminUserStats {
  total: number;
  banned: number;
  new_today: number;
  new_week: number;
}

/** 概览消息维度计数 */
export interface AdminMessageStats {
  total: number;
  today: number;
  by_type: Record<string, number>;
}

/** 概览治理队列积压计数 */
export interface AdminModerationStats {
  pending_reports: number;
  flagged_messages: number;
  pending_ugc: number;
  taken_down_packs: number;
  flagged_packs: number;
}

/** 概览增长侧写计数（好友申请 / 验证码下发，今日与近 7 天） */
export interface AdminGrowthStats {
  friend_requests_today: number;
  friend_requests_week: number;
  otp_today: number;
  otp_week: number;
}

/** 管理端运营概览聚合指标（GET /admin/stats 响应体） */
export interface AdminStats {
  users: AdminUserStats;
  conversations: { total: number };
  messages: AdminMessageStats;
  moderation: AdminModerationStats;
  growth: AdminGrowthStats;
  runtime: { online_connections: number };
}

/** 拉取运营概览聚合指标（只读快照） */
export function getStats() {
  return pagedGetSingle<AdminStats>("/api/v1/admin/stats");
}

/** 单一对象类别的存储统计行（bytes 未知为 null） */
export interface StorageStat {
  category: string;
  object_count: number;
  total_bytes: number | null;
}

/** 存储统计响应（GET /admin/storage-stats，DB 聚合口径） */
export interface StorageStats {
  categories: StorageStat[];
}

/** 拉取按对象类别的存储占用统计（只读） */
export function getStorageStats() {
  return pagedGetSingle<StorageStats>("/api/v1/admin/storage-stats");
}

/** 管理端推送订阅视图（附所属用户昵称） */
export interface AdminPushSubscription {
  id: string;
  user_id: string;
  endpoint: string;
  created_at: string;
  user_nickname?: string | null;
}

/** 分页列出推送订阅（最新在前） */
export function listPushSubscriptions(page: number, size = 10) {
  return pagedGet<AdminPushSubscription>(`/api/v1/admin/push-subscriptions${qs({ page, size })}`);
}

/** 时间序列单日数据点（空日已在后端补零） */
export interface TimeseriesPoint {
  /** 日期字符串，格式 2006-01-02 */
  date: string;
  messages: number;
  new_users: number;
  new_conversations: number;
}

/** 时间序列响应体（GET /admin/stats/timeseries） */
export interface StatsTimeseriesResult {
  /** 请求的窗口天数（与请求参数一致） */
  days: number;
  /** 逐日数据点，长度恒等于 days，日期升序（最旧在前） */
  points: TimeseriesPoint[];
}

/** 拉取运营概览时间序列（7–90 天，空日后端已补零） */
export function getTimeseries(days = 30) {
  return pagedGetSingle<StatsTimeseriesResult>(`/api/v1/admin/stats/timeseries${qs({ days })}`);
}
